import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import ProductCard from "@/components/ProductCard";
import { api } from "@/lib/api";
import { Star, MapPin, Users, Mail } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function Storefront() {
  const { slug } = useParams();
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/merchants/by-slug/${slug}`).then(async ({ data }) => {
      setStore(data);
      const { data: prods } = await api.get(`/merchants/${data.merchant_id}/products`);
      setProducts(prods);
    }).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <Layout><div className="p-16 text-center text-[#5C5A56]">Loading store…</div></Layout>;
  if (!store) return <Layout><div className="p-16 text-center">Store not found</div></Layout>;

  return (
    <Layout>
      {/* Banner */}
      <div className="relative h-56 md:h-80 overflow-hidden" data-testid="storefront-banner">
        {store.banner && <img src={store.banner} alt={store.name} className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/10" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 -mt-16 relative">
        <div className="surface p-6 md:p-8 grid md:grid-cols-12 gap-6 items-center">
          {store.logo && (
            <div className="md:col-span-2">
              <img src={store.logo} alt={store.name} className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover border-2 border-white -mt-12 shadow" />
            </div>
          )}
          <div className="md:col-span-7">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              {store.types?.map((t) => <span key={t} className="tag-pill bg-[#F2E8E9] text-[#702A32]">{t}</span>)}
            </div>
            <h1 className="font-heading text-3xl md:text-4xl tracking-tight" data-testid="storefront-name">{store.name}</h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-[#5C5A56] mt-2">
              <span className="inline-flex items-center gap-1.5"><Star className="w-4 h-4 fill-[#C78B1B] text-[#C78B1B]" strokeWidth={1.5} /> {store.rating?.toFixed(2)} · {store.review_count} reviews</span>
              <span className="inline-flex items-center gap-1.5"><Users className="w-4 h-4" strokeWidth={1.5} /> {store.follower_count} followers</span>
              <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" strokeWidth={1.5} /> {store.location}</span>
            </div>
          </div>
          <div className="md:col-span-3 flex md:justify-end gap-2">
            <button className="btn-secondary" data-testid="follow-store-btn">Follow</button>
            <button className="btn-primary inline-flex items-center gap-2" data-testid="message-store-btn">
              <Mail className="w-4 h-4" strokeWidth={1.5} /> Message
            </button>
          </div>
        </div>
      </div>

      <Tabs defaultValue="products" className="max-w-7xl mx-auto px-6 md:px-12 mt-10">
        <TabsList className="bg-transparent border-b border-[#E5E3DD] rounded-none w-full justify-start" data-testid="storefront-tabs">
          <TabsTrigger value="products" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#702A32] data-[state=active]:bg-transparent data-[state=active]:text-[#702A32]">Products</TabsTrigger>
          <TabsTrigger value="about" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#702A32] data-[state=active]:bg-transparent data-[state=active]:text-[#702A32]">About</TabsTrigger>
          <TabsTrigger value="reviews" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#702A32] data-[state=active]:bg-transparent data-[state=active]:text-[#702A32]">Reviews</TabsTrigger>
          <TabsTrigger value="policies" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#702A32] data-[state=active]:bg-transparent data-[state=active]:text-[#702A32]">Policies</TabsTrigger>
          <TabsTrigger value="contact" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#702A32] data-[state=active]:bg-transparent data-[state=active]:text-[#702A32]">Contact</TabsTrigger>
        </TabsList>
        <TabsContent value="products" className="pt-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-testid="storefront-products">
            {products.map((p) => <ProductCard key={p.product_id} product={p} />)}
          </div>
        </TabsContent>
        <TabsContent value="about" className="pt-8 max-w-3xl">
          <p className="text-[#5C5A56] leading-relaxed font-light">{store.description}</p>
        </TabsContent>
        <TabsContent value="reviews" className="pt-8 max-w-3xl">
          <p className="text-[#5C5A56] font-light">{store.review_count} customer reviews across {products.length} products. Average rating: {store.rating?.toFixed(2)} / 5.</p>
        </TabsContent>
        <TabsContent value="policies" className="pt-8 max-w-3xl">
          <p className="text-[#5C5A56] leading-relaxed font-light whitespace-pre-line">{store.policies || "This merchant has not published custom policies yet."}</p>
        </TabsContent>
        <TabsContent value="contact" className="pt-8 max-w-2xl">
          <p className="text-[#5C5A56] font-light mb-4">Send a message to {store.name} — they typically reply within a business day.</p>
          <textarea className="input-field min-h-[120px]" placeholder="Write your message…" />
          <button className="btn-primary mt-3">Send message</button>
        </TabsContent>
      </Tabs>
      <div className="pb-20" />
    </Layout>
  );
}
